import {
  FaFacebook,
  FaInstagram,
  FaLinkedinIn,
  FaTelegram,
  FaXTwitter,
  FaYoutube,
} from 'react-icons/fa6';
import { Link, useLocation } from 'react-router-dom';

import { useIsMobile } from '@/lib/hooks/useMobile';
import { cn } from '@/lib/utils/cn';
import { useChatStore, useSettingsStore } from '@/store';
import ClientSwitcher from '../shared/ClientSwitcher';
import useMenuItems from '@/lib/hooks/useMenuItems';

const socialIcons: Record<string, JSX.Element> = {
  x: <FaXTwitter size={18} className="text-muted-foreground" />,
  linkedin: <FaLinkedinIn size={18} className="text-muted-foreground" />,
  instagram: <FaInstagram size={18} className="text-muted-foreground" />,
  facebook: <FaFacebook size={18} className="text-muted-foreground" />,
  youtube: <FaYoutube size={18} className="text-muted-foreground" />,
  telegram: <FaTelegram size={18} className="text-muted-foreground" />,
};

export const Footer = () => {
  const { pathname } = useLocation();

  const { chatInputFocused } = useChatStore();
  const hideFooter = pathname?.startsWith('/chat');
  const isMobile = useIsMobile();

  const { settings } = useSettingsStore();
  const socialLinks = settings?.ui?.links?.socialLinks || [];
  const footerLinks = settings?.ui?.links?.footerLinks || [];

  // const filteredAppLinks = filterFooterAppLinks(appLinks, allowedPages);

  const { footerMenuLinks } = useMenuItems();

  if (hideFooter && isMobile && chatInputFocused) {
    return null;
  }

  return (
    <footer className={cn('border-t', chatInputFocused ? 'mt-0' : 'mt-8')}>
      {/* Main Footer Section */}
      {!chatInputFocused && (
        <div className="container mx-auto py-12 px-4">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-12">
            {/* Brand and description */}
            <div className="md:col-span-4">
              <h2 className="text-xl font-bold mb-4 ">
                {settings?.ui?.organizationName}
              </h2>
              <p className="text-muted-foreground mb-6 max-w-md text-xs sm:text-sm break-words whitespace-pre-wrap">
                {settings?.ui?.orgDescription}
              </p>
              <div className="flex space-x-4">
                {socialLinks.map((link) => (
                  <a
                    key={link.platform}
                    href={link.url}
                    target="_blank"
                    rel="noreferrer"
                    className="hover:text-primary transition-colors"
                    aria-label={`${link.platform} link`}
                  >
                    {socialIcons[link.platform]}
                  </a>
                ))}
              </div>
            </div>

            <div className="md:col-span-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
                {footerMenuLinks.sections.map((section) => (
                  <div key={section.title}>
                    <h3 className="text-sm font-semibold uppercase tracking-wider mb-4">
                      {section.title}
                    </h3>
                    <ul className="space-y-2">
                      {section.links.map((link) => (
                        <li key={link.label}>
                          <Link
                            to={link.url}
                            className="text-muted-foreground hover:text-primary transition-colors text-sm"
                          >
                            {link.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sub Footer */}
      <div className="border-t">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} {settings?.ui?.organizationName} All
              rights reserved.
            </div>
            <div className="flex flex-wrap items-center gap-6">
              {footerLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                  data-testid="footer-links-2"
                >
                  {link.label}
                </a>
              ))}

              <ClientSwitcher />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
